// A Radar showing a team (?live=1; teams and matches, Ben 2026-10-30 — docs/modules/
// teams-and-matches.md "5. Radar"): one Radar, player or team. Two team templates to start, as
// Catalyst: Attacking (the team's own numbers) and Defending (its opponents' numbers, turned round
// so a bigger shape = better defending). Each spoke runs from the lowest to the highest team in the
// leagues on the Radar, that season (not hand-set numbers). The numbers are /team-stats answers,
// one per league season and stats category, as the Stats tiles read them.
//
// A spoke is named "category:key" ("attacking:xg_90"), so a Radar knows which answers to ask for
// before it has any. The spoke editor changes a template's list (`_teamRadarSpokes`, kept per
// template, as the player Radar keeps its own); Reset goes back to the ten below.

import type { TeamStatsResponse } from "@catbento/shared";
import type { DistSpread, RadarAxisWithRange } from "../modules/radar-view.js";
import { metricFraction } from "../modules/radar-view.js";
import { teamComparisonTrack, teamLowerIsBetter } from "./team-stats.js";

export type TeamRadarTemplate = "attacking" | "defending";
export const TEAM_RADAR_TEMPLATES: readonly { value: TeamRadarTemplate; label: string }[] = [
  { value: "attacking", label: "Attacking" },
  { value: "defending", label: "Defending" },
];

/** The /team-stats categories the spoke editor lists (the engine's team groups; Appearance is
 *  minutes only, nothing per game). */
export const TEAM_STAT_CATEGORIES: readonly { id: string; name: string }[] = [
  { id: "attacking", name: "Attacking" },
  { id: "passing", name: "Passing" },
  { id: "defending", name: "Defending" },
  { id: "possession", name: "Possession" },
  { id: "set_pieces", name: "Set Pieces" },
  { id: "discipline", name: "Discipline" },
  { id: "goalkeeping", name: "Goalkeeping" },
];

/** Each template's own spokes, in order round the wheel. */
const DEFAULT_SPOKES: Readonly<Record<TeamRadarTemplate, readonly string[]>> = {
  attacking: [
    "attacking:team_goals_90",
    "attacking:xg_90",
    "attacking:shots_90",
    "attacking:np_xg_per_shot",
    "attacking:shots_in_clear_90",
    "attacking:obv_90",
    "attacking:dribbles_successful_90",
    "passing:deep_completions_90",
    "passing:passes_inside_box_90",
    "passing:crosses_into_box_successful_90",
  ],
  defending: [
    "attacking:opponent_team_goals_90",
    "attacking:opponent_xg_90",
    "attacking:opponent_shots_90",
    "attacking:opponent_np_xg_per_shot",
    "attacking:opponent_shots_in_clear_90",
    "attacking:opponent_obv_90",
    "attacking:opponent_dribbles_successful_90",
    "passing:opponent_deep_completions_90",
    "passing:opponent_passes_inside_box_90",
    "passing:opponent_crosses_into_box_successful_90",
  ],
};

/** A stored template, or Attacking. Pure. */
export function teamRadarTemplate(stored: unknown): TeamRadarTemplate {
  return stored === "defending" ? "defending" : "attacking";
}

/** A template's own ten. Pure. */
export function defaultTeamSpokes(template: TeamRadarTemplate): string[] {
  return [...DEFAULT_SPOKES[template]];
}

/** The spokes a template shows: the editor's picks for it (`_teamRadarSpokes`), else its ten. Pure. */
export function teamRadarSpokes(template: TeamRadarTemplate, stored: unknown): string[] {
  const picks = (stored as Partial<Record<TeamRadarTemplate, unknown>> | null | undefined)?.[template];
  if (picks === undefined || picks === null) return defaultTeamSpokes(template);
  if (!Array.isArray(picks) || !picks.every((p) => typeof p === "string" && p.includes(":"))) {
    throw new Error(`A team Radar's spokes aren't "category:key" names: ${JSON.stringify(picks)}`);
  }
  return picks as string[];
}

/** "attacking:xg_90" → its category and the engine's key. Pure. */
export function spokeParts(spoke: string): { category: string; key: string } {
  const at = spoke.indexOf(":");
  return { category: spoke.slice(0, at), key: spoke.slice(at + 1) };
}

/** The categories the spokes need, each once, in order. Pure. */
export function spokeCategories(spokes: readonly string[]): string[] {
  return [...new Set(spokes.map((s) => spokeParts(s).category))];
}

/** A team Radar spoke: the chart's axis (keyed by the engine's key), with which way is better. */
export type TeamRadarAxis = RadarAxisWithRange & { lowerBetter: boolean };

/** The spokes with their names (the engine's short names, as the Stats tiles show them) and their
 *  range: the lowest to the highest team in every answer given (one per league season on the
 *  Radar; two teams from one league share theirs). A spoke none of the answers name is left out;
 *  one with fewer than two values reads 0–0 (mid-spoke). Pure. */
export function teamRadarAxes(spokes: readonly string[], answers: readonly (TeamStatsResponse | null)[]): TeamRadarAxis[] {
  const given = answers.filter((a): a is TeamStatsResponse => a !== null);
  const metrics = new Map(given.flatMap((a) => [...a.for, ...a.against]).flatMap((g) => g.metrics).map((m) => [m.key, m]));
  return spokes.flatMap((spoke) => {
    const { key } = spokeParts(spoke);
    const m = metrics.get(key);
    if (!m) return [];
    const range = teamComparisonTrack(given, key) ?? { min: 0, max: 0 };
    return [{ key, label: m.shortName || m.name, outputType: m.outputType, lowerBetter: teamLowerIsBetter(key), ...range }];
  });
}

/** One team's values on the spokes, from the answers for its league season (null = none). Pure. */
export function teamRadarValues(axes: readonly TeamRadarAxis[], answers: readonly (TeamStatsResponse | null)[], teamId: number): (number | null)[] {
  return axes.map((a) => {
    for (const ans of answers) {
      const v = ans?.rows.find((r) => r.teamId === teamId)?.values[a.key];
      if (v !== undefined && v !== null && !Number.isNaN(v)) return v;
    }
    return null;
  });
}

/** Where each value sits on its spoke, 0–1, turned round where less is better. Pure. */
export function teamRadarFractions(axes: readonly TeamRadarAxis[], values: readonly (number | null)[]): (number | null)[] {
  return axes.map((a, i) => metricFraction(values[i], a, a.lowerBetter));
}

/** The editor's "Add a metric" list: every category's own numbers on Attacking, its opponents' on
 *  Defending, each named "category:key". A category not loaded yet is left out. Pure. */
export function teamEditorGroups(
  template: TeamRadarTemplate,
  byCategory: readonly { id: string; name: string; answer: TeamStatsResponse | null }[],
): { id: string; name: string; metrics: { key: string; name: string; shortName: string }[] }[] {
  return byCategory.flatMap(({ id, name, answer }) => {
    const groups = answer ? answer[template === "defending" ? "against" : "for"] : [];
    const metrics = groups.flatMap((g) => g.metrics).map((m) => ({ key: `${id}:${m.key}`, name: m.name, shortName: m.shortName }));
    return metrics.length ? [{ id, name, metrics }] : [];
  });
}

/** A Distribution's group for teams: each stat's values across every team in the answers given
 *  (each league season once), and the header's words. Pure. */
export function teamSpread(answers: readonly (TeamStatsResponse | null)[], groupWords: string): DistSpread {
  const unique = [...new Set(answers.filter((a): a is TeamStatsResponse => a !== null))];
  // Each league season's teams once, though it answers several categories.
  const teams = new Set(unique.flatMap((a) => a.rows.map((r) => r.teamId)));
  return {
    valuesOf: (key) =>
      unique
        .flatMap((a) => a.rows.map((r) => r.values[key]))
        .filter((v): v is number => v !== null && v !== undefined && !Number.isNaN(v))
        .sort((a, b) => a - b),
    lowerBetter: teamLowerIsBetter,
    sub: `vs ${groupWords} · ${teams.size} teams`,
  };
}
